/**
 * Knowledge Sidebar for the public interview page.
 *
 * Displays pre-loaded company synthesis, per-topic knowledge items,
 * live insight updates from SSE, and inline correction editing.
 * Supports highlighting specific fields when opened from a KnowledgeUpdateBanner.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronDown, ChevronRight, Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SidebarKnowledge, TopicKnowledge, KnowledgeDelta } from '@/lib/api/publicInterview';

// ========================================
// TYPES
// ========================================

interface KnowledgeSidebarProps {
  knowledge: SidebarKnowledge | null;
  currentTopicId: string | null;
  onSaveCorrection: (topicId: string | null, fieldPath: string, correctedValue: any) => void;
  highlightedDelta?: KnowledgeDelta | null;
  highlightKey?: number;
}

// ========================================
// INLINE EDIT FIELD
// ========================================

const InlineEditField: React.FC<{
  label: string;
  value: string;
  fieldPath: string;
  onSave: (fieldPath: string, value: string) => void;
  isHighlighted?: boolean;
}> = ({ label, value, fieldPath, onSave, isHighlighted }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) {
      onSave(fieldPath, trimmed);
    }
    setEditing(false);
  }, [editValue, value, fieldPath, onSave]);

  const handleCancel = useCallback(() => {
    setEditValue(value);
    setEditing(false);
  }, [value]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  if (editing) {
    return (
      <div className="space-y-1">
        <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
        <div className="space-y-1.5">
          <textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            rows={Math.max(2, Math.ceil(editValue.length / 40))}
            className="w-full rounded border border-input px-2 py-1.5 text-xs text-foreground bg-background outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 resize-y"
          />
          <div className="flex items-center gap-1.5 justify-end">
            <button
              onClick={handleCancel}
              className="p-1 rounded hover:bg-muted text-muted-foreground transition-colors"
              title="Cancel"
            >
              <X className="h-3 w-3" />
            </button>
            <button
              onClick={handleSave}
              className="p-1 rounded hover:bg-primary/10 text-primary transition-colors"
              title="Save"
            >
              <Check className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-highlight={isHighlighted ? 'true' : undefined}
      className={cn(
        'group space-y-0.5 rounded px-1.5 py-0.5 -mx-1.5 transition-colors',
        isHighlighted && 'bg-primary/10'
      )}
    >
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="flex items-start gap-1.5">
        <p className="flex-1 text-xs text-foreground leading-relaxed">{value}</p>
        <button
          onClick={() => {
            setEditValue(value);
            setEditing(true);
          }}
          className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted text-muted-foreground transition-all shrink-0 mt-0.5"
          title="Edit"
        >
          <Pencil className="h-2.5 w-2.5" />
        </button>
      </div>
    </div>
  );
};

// ========================================
// COMPANY SUMMARY SECTION
// ========================================

const CompanySummarySection: React.FC<{
  summary: SidebarKnowledge['company_summary'];
  onEdit: (topicId: string | null, fieldPath: string, value: string) => void;
}> = ({ summary, onEdit }) => {
  const handleEdit = useCallback(
    (fieldPath: string, value: string) => onEdit(null, fieldPath, value),
    [onEdit]
  );
  const hasContent = summary.name || summary.one_liner || summary.category;
  if (!hasContent) return null;

  return (
    <div className="space-y-2.5">
      <h3 className="text-xs font-semibold text-foreground">Company</h3>
      <div className="space-y-2 rounded-lg border border-border bg-background/50 p-3">
        {summary.name && (
          <InlineEditField
            label="Name"
            value={summary.name}
            fieldPath="company_profile.name"
            onSave={handleEdit}
          />
        )}
        {summary.one_liner && (
          <InlineEditField
            label="Description"
            value={summary.one_liner}
            fieldPath="company_profile.one_liner"
            onSave={handleEdit}
          />
        )}
        {summary.category && (
          <InlineEditField
            label="Category"
            value={summary.category}
            fieldPath="company_profile.category"
            onSave={handleEdit}
          />
        )}
      </div>
    </div>
  );
};

// ========================================
// TOPIC SECTION
// ========================================

const TopicSection: React.FC<{
  topicId: string;
  topic: TopicKnowledge;
  isActive: boolean;
  onSaveCorrection: (topicId: string | null, fieldPath: string, value: any) => void;
  highlightedFields?: Set<string>;
  initialCollapsed?: boolean;
}> = ({ topicId, topic, isActive, onSaveCorrection, highlightedFields, initialCollapsed }) => {
  const hasHighlight = highlightedFields && highlightedFields.size > 0;
  const [expanded, setExpanded] = useState(!initialCollapsed && (isActive || !!hasHighlight));

  // Bind topicId so downstream components don't need to know it
  const handleEdit = useCallback(
    (fieldPath: string, value: any) => onSaveCorrection(topicId, fieldPath, value),
    [topicId, onSaveCorrection]
  );

  // Auto-expand when this topic gets highlighted
  useEffect(() => {
    if (hasHighlight) setExpanded(true);
  }, [hasHighlight]);

  const sectionEntries = Object.entries(topic.synthesis_sections);
  const hasContent = sectionEntries.length > 0 || topic.live_insights.length > 0;
  if (!hasContent) return null;

  return (
    <div className="space-y-1.5">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className={cn(
          'w-full flex items-center gap-2 text-left text-xs font-semibold transition-colors',
          isActive ? 'text-primary' : 'text-foreground hover:text-primary'
        )}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        <span className="truncate">{topic.topic_name}</span>
        {isActive && (
          <span className="ml-auto text-[10px] font-normal text-primary/60 shrink-0">
            Current
          </span>
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="space-y-2 rounded-lg border border-border bg-background/50 p-3">
              {/* Synthesis sections */}
              {sectionEntries.map(([sectionKey, sectionData]) => (
                <SynthesisSectionBlock
                  key={sectionKey}
                  sectionKey={sectionKey}
                  data={sectionData}
                  onEdit={handleEdit}
                  highlightedFields={highlightedFields}
                />
              ))}

              {/* Live insights */}
              {topic.live_insights.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    From Interview
                  </span>
                  <AnimatePresence>
                    {topic.live_insights.map((insight) => {
                      const isHighlighted = highlightedFields?.has(insight.field);
                      return (
                        <motion.div
                          key={insight.field}
                          data-highlight={isHighlighted ? 'true' : undefined}
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className={cn(
                            'flex items-start gap-1.5 rounded px-1.5 py-0.5 -mx-1.5 transition-colors',
                            isHighlighted && 'bg-primary/10'
                          )}
                        >
                          <span className="text-[10px] text-primary font-medium mt-0.5 shrink-0">
                            New
                          </span>
                          <div className="min-w-0">
                            <span className="text-[10px] text-muted-foreground">
                              {insight.field.replace(/_/g, ' ')}
                            </span>
                            <p className="text-xs text-foreground leading-relaxed">
                              {formatSynthesisValue(insight.value) ?? String(insight.value)}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ========================================
// SYNTHESIS SECTION BLOCK
// ========================================

/**
 * Formats a synthesis value into a human-readable string.
 * Handles strings, arrays of strings, arrays of objects, and nested objects.
 */
function formatSynthesisValue(value: any): string | null {
  if (typeof value === 'string') {
    return value.trim() || null;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    if (typeof value[0] === 'string') {
      return value.filter(Boolean).join(', ');
    }
    // Array of objects — extract the most useful text field from each
    const texts = value
      .map((item) => {
        if (typeof item === 'string') return item;
        if (typeof item !== 'object' || !item) return null;
        return (
          item.name || item.statement || item.scenario || item.title ||
          item.objection || item.area || item.question || item.description || null
        );
      })
      .filter(Boolean);
    return texts.length > 0 ? texts.join(', ') : null;
  }
  if (typeof value === 'object' && value !== null) {
    const parts = Object.entries(value)
      .filter(([k, v]) => typeof v === 'string' && (v as string).trim() && k !== 'confidence' && k !== 'sources')
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`);
    return parts.length > 0 ? parts.join(' | ') : null;
  }
  return null;
}

const SynthesisSectionBlock: React.FC<{
  sectionKey: string;
  data: any;
  onEdit: (fieldPath: string, value: any) => void;
  highlightedFields?: Set<string>;
}> = ({ sectionKey, data, onEdit, highlightedFields }) => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const entries = Object.entries(data).filter(
    ([key]) => key !== 'confidence' && key !== 'sources' && key !== 'source_map'
  );

  const renderable = entries
    .map(([fieldName, fieldValue]) => {
      const formatted = formatSynthesisValue(fieldValue);
      if (!formatted) return null;
      return { fieldName, formatted, isString: typeof fieldValue === 'string' };
    })
    .filter(Boolean) as { fieldName: string; formatted: string; isString: boolean }[];

  if (renderable.length === 0) return null;

  return (
    <div className="space-y-1.5">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
        {sectionKey.replace(/_/g, ' ')}
      </span>
      {renderable.map(({ fieldName, formatted, isString }) => {
        const isHighlighted = highlightedFields?.has(fieldName);
        return isString ? (
          <InlineEditField
            key={fieldName}
            label={fieldName.replace(/_/g, ' ')}
            value={formatted}
            fieldPath={`${sectionKey}.${fieldName}`}
            onSave={onEdit}
            isHighlighted={isHighlighted}
          />
        ) : (
          <div
            key={fieldName}
            data-highlight={isHighlighted ? 'true' : undefined}
            className={cn(
              'space-y-0.5 rounded px-1.5 py-0.5 -mx-1.5 transition-colors',
              isHighlighted && 'bg-primary/10'
            )}
          >
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
              {fieldName.replace(/_/g, ' ')}
            </span>
            <p className="text-xs text-foreground leading-relaxed">{formatted}</p>
          </div>
        );
      })}
    </div>
  );
};

// ========================================
// MAIN COMPONENT
// ========================================

const KnowledgeSidebar: React.FC<KnowledgeSidebarProps> = ({
  knowledge,
  currentTopicId,
  onSaveCorrection,
  highlightedDelta,
  highlightKey,
}) => {
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  // Build a set of highlighted field names from the delta
  const highlightedTopicId = highlightedDelta?.topic_id ?? null;
  const highlightedFields = React.useMemo(() => {
    if (!highlightedDelta) return null;
    const fields = new Set<string>();
    for (const f of highlightedDelta.updated_fields) fields.add(f);
    for (const i of highlightedDelta.new_insights) fields.add(i.field);
    return fields.size > 0 ? fields : null;
  }, [highlightedDelta]);

  // Scroll to first highlighted element via DOM query
  useEffect(() => {
    if (!highlightedFields) return;
    const timer = setTimeout(() => {
      const el = sidebarRef.current?.querySelector('[data-highlight="true"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300); // wait for topic expand animation
    return () => clearTimeout(timer);
  }, [highlightedFields, highlightKey]);


  if (!knowledge) return null;

  const currentTopic = currentTopicId ? knowledge.topics[currentTopicId] : null;
  const otherTopics = Object.entries(knowledge.topics).filter(
    ([id]) => id !== currentTopicId
  );

  return (
    <div className="h-full flex flex-col bg-card">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">Knowledge</h2>
        </div>
      </div>

      {/* Content */}
      <div ref={sidebarRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {/* Company Summary */}
        <CompanySummarySection summary={knowledge.company_summary} onEdit={onSaveCorrection} />

        {/* Current topic (expanded) */}
        {currentTopicId && currentTopic && (
          <TopicSection
            topicId={currentTopicId}
            topic={currentTopic}
            isActive
            onSaveCorrection={onSaveCorrection}
            highlightedFields={highlightedTopicId === currentTopicId ? (highlightedFields ?? undefined) : undefined}
          />
        )}

        {/* Other topics (collapsed) */}
        {otherTopics.map(([id, topic]) => (
          <TopicSection
            key={id}
            topicId={id}
            topic={topic}
            isActive={false}
            onSaveCorrection={onSaveCorrection}
            highlightedFields={highlightedTopicId === id ? (highlightedFields ?? undefined) : undefined}
          />
        ))}

        {/* Empty state */}
        {!knowledge.company_summary.name &&
          Object.keys(knowledge.topics).length === 0 && (
            <div className="text-center py-6">
              <p className="text-xs text-muted-foreground">
                No pre-loaded knowledge available yet.
              </p>
            </div>
          )}
      </div>
    </div>
  );
};

export { KnowledgeSidebar, CompanySummarySection, TopicSection };
